import axios from 'axios';
import { buildSiteFormData, hasSiteMediaFile } from '../utils/siteFormData';
import {
  isOnPublicAuthRoute,
  shouldScheduleOfflineFlush as shouldScheduleOfflineFlushForRoute,
} from '../utils/authPublicRoutes';
import {
  isRetryableCriticalMutation,
  recordApiSuccess,
  recordGatewayFailure,
  retryCriticalMutation,
} from './deployResilience';
import {
  isHardAuthLogoutError,
  isHardRefreshFailure,
  isTransientRefreshFailure,
} from '../utils/sessionAuthErrors';
import {
  enqueueTerrainOp,
  flushTerrainOutbox,
  clearTerrainOutbox,
  rebuildAxiosConfigData,
  listTerrainOps,
  countPendingTerrainOps,
  removeTerrainOp,
} from '../utils/terrainOutboxIdb';
import {
  putApiReadCacheEntry,
  getApiReadCacheEntry,
  clearApiReadCache,
  findApiReadCacheByUrlPath,
} from '../utils/apiReadCacheIdb';
import {
  extractReportImageRows,
  isInterventionReportImageContentPath,
  matchApiMediaCache,
  putApiMediaCacheFromBlob,
  warmApiMediaUrl,
} from '../utils/apiMediaCache';
import {
  extractClientDocumentRows,
  warmUploadsUrl,
} from '../utils/uploadsCache';
import {
  buildOfflineQueuedResponseData,
  rememberOptimisticFromQueuedConfig,
  clearAllOptimisticInterventionStatuses,
  clearOptimisticInterventionStatus,
  parseInterventionIdFromWorkflowUrl,
  isWorkflowMutationUrl,
  isIntervenantReportMutationUrl,
} from '../utils/terrainOfflineHelpers';

function normalizeApiBaseUrl(rawValue) {
  const raw = String(rawValue || '').trim();
  if (!raw) return '/api';

  // Chemin relatif explicite
  if (raw.startsWith('/')) {
    const normalizedPath = raw.replace(/\/+$/, '');
    return normalizedPath === '' || normalizedPath === '/' ? '/api' : normalizedPath;
  }

  // URL absolue (Vercel/Render)
  try {
    const parsed = new URL(raw);
    const cleanPath = parsed.pathname.replace(/\/+$/, '');
    if (!cleanPath || cleanPath === '/') {
      // Backend UCP expose ses routes sous /api
      parsed.pathname = '/api';
    } else {
      parsed.pathname = cleanPath;
    }
    return parsed.toString().replace(/\/+$/, '');
  } catch {
    // Fallback conservateur si valeur invalide
    return '/api';
  }
}

// En dev: proxy CRA '/api'. En prod: URL backend normalisée.
const API_BASE_URL = normalizeApiBaseUrl(process.env.REACT_APP_API_URL);

export const getApiBaseUrl = () => API_BASE_URL;

/**
 * Origine HTTP du backend pour les assets hors /api (ex. /uploads/rapports).
 * Ne jamais préfixer les chemins /api/... (miniatures) : utiliser resolveAssetUrlForDisplay()
 * dans frontend/src/utils/attachmentAssetUrl.js.
 * Sur Vercel, REACT_APP_API_URL peut être relatif (/api) : /uploads/... doit pointer vers Render.
 */
export function getApiPublicOrigin() {
  const pub = String(process.env.REACT_APP_API_PUBLIC_ORIGIN || '').trim();
  if (/^https?:\/\//i.test(pub)) {
    try {
      return new URL(pub).origin;
    } catch {
      /* ignore */
    }
  }
  const raw = String(process.env.REACT_APP_API_URL || '').trim();
  if (/^https?:\/\//i.test(raw)) {
    try {
      return new URL(raw).origin;
    } catch {
      /* ignore */
    }
  }
  const backend = String(process.env.REACT_APP_BACKEND_URL || '').trim();
  if (/^https?:\/\//i.test(backend)) {
    try {
      return new URL(backend).origin;
    } catch {
      /* ignore */
    }
  }
  return null;
}

// Configuration axios par défaut
const api = axios.create({
  baseURL: API_BASE_URL,
  withCredentials: true,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Utilitaire pour définir/enlever le header Authorization globalement
export function setAuthToken(token) {
  if (token) {
    api.defaults.headers.common['Authorization'] = `Bearer ${token}`;
  } else {
    delete api.defaults.headers.common['Authorization'];
  }
}

function _safeGetStorageItem(key) {
  try {
    const fromLocal = localStorage.getItem(key);
    if (fromLocal !== null && fromLocal !== undefined) return fromLocal;
  } catch {
    // ignore localStorage read failure (Safari private mode/security settings)
  }
  try {
    const fromSession = sessionStorage.getItem(key);
    if (fromSession !== null && fromSession !== undefined) return fromSession;
  } catch {
    // ignore sessionStorage read failure
  }
  return null;
}

// Initialisation : Bearer seulement hors routes login publiques (évite 401 + reload sur `/`).
const existingToken = _safeGetStorageItem('token');
const onPublicAuthAtLoad =
  typeof window !== 'undefined' && isOnPublicAuthRoute(window.location.pathname);
if (existingToken && !onPublicAuthAtLoad) {
  setAuthToken(existingToken);
}

// ─── ✅ OFFLINE-001 : Cache lecture + file d'attente hors-ligne ─────────────
const OFFLINE_CACHE_PREFIX = 'ucp_api_cache_v1';
const OFFLINE_QUEUE_KEY = 'ucp_api_queue_v1';
const OFFLINE_QUEUE_MAX = 200;
const OFFLINE_WARMUP_INTERVAL_MS = 2 * 60 * 1000;
const OFFLINE_WARMUP_PROD_INTERVAL_MS = 45 * 60 * 1000;
const OFFLINE_SNAPSHOT_META_KEY = 'ucp_offline_snapshot_meta_v1';
const OFFLINE_CACHE_MAX_ENTRIES = 120;
const OFFLINE_CACHE_MAX_ENTRY_BYTES = 180 * 1024;
const OFFLINE_CACHE_MAX_TOTAL_BYTES = 2.5 * 1024 * 1024;
let _isFlushingOfflineQueue = false;
let _isWarmingOfflineCache = false;
/** Hit RAM immédiat (listes lourdes absentes du LS si trop volumineuses). */
const _memoryGetCache = new Map();
const MEMORY_GET_CACHE_MAX = 40;
const STORAGE_QUOTA_ERROR_CODES = new Set([22, 1014]);

function _isStorageQuotaError(error) {
  if (!error) return false;
  if (STORAGE_QUOTA_ERROR_CODES.has(error.code)) return true;
  const name = String(error.name || '').toLowerCase();
  if (name.includes('quota')) return true;
  const message = String(error.message || '').toLowerCase();
  return message.includes('quota') || message.includes('exceeded');
}

function _getOfflineCacheEntries() {
  const entries = [];
  try {
    for (let i = 0; i < localStorage.length; i += 1) {
      const key = localStorage.key(i);
      if (!key || !key.startsWith(`${OFFLINE_CACHE_PREFIX}:`)) continue;
      const raw = localStorage.getItem(key);
      const parsed = raw ? _safeJsonParse(raw, null) : null;
      entries.push({
        key,
        timestamp: parsed?.timestamp || 0,
        bytes: raw ? raw.length * 2 : 0
      });
    }
  } catch {
    return [];
  }
  return entries.sort((a, b) => a.timestamp - b.timestamp);
}

function _pruneOfflineCacheToLimits({
  maxEntries = OFFLINE_CACHE_MAX_ENTRIES,
  maxTotalBytes = OFFLINE_CACHE_MAX_TOTAL_BYTES
} = {}) {
  const entries = _getOfflineCacheEntries();
  let currentEntries = entries.length;
  let currentTotalBytes = entries.reduce((sum, item) => sum + (item.bytes || 0), 0);

  for (let i = 0; i < entries.length; i += 1) {
    const overEntries = currentEntries > maxEntries;
    const overBytes = currentTotalBytes > maxTotalBytes;
    if (!overEntries && !overBytes) break;

    localStorage.removeItem(entries[i].key);
    currentEntries -= 1;
    currentTotalBytes -= (entries[i].bytes || 0);
  }
}

function _cleanupOfflineStorage() {
  _pruneOfflineCacheToLimits({
    maxEntries: Math.max(20, Math.floor(OFFLINE_CACHE_MAX_ENTRIES * 0.6)),
    maxTotalBytes: Math.max(400 * 1024, Math.floor(OFFLINE_CACHE_MAX_TOTAL_BYTES * 0.6))
  });
}

export function safeLocalStorageSetItem(key, value) {
  try {
    localStorage.setItem(key, value);
    return true;
  } catch (error) {
    if (!_isStorageQuotaError(error)) {
      return false;
    }
  }

  try {
    _cleanupOfflineStorage();
    localStorage.setItem(key, value);
    return true;
  } catch {
    return false;
  }
}

function _safeJsonParse(value, fallback) {
  try {
    return JSON.parse(value);
  } catch {
    return fallback;
  }
}

function _getOfflineScope() {
  const userRaw = _safeGetStorageItem('user');
  const user = userRaw ? _safeJsonParse(userRaw, null) : null;
  if (user?.id) return `user-${user.id}`;
  if (user?.email) return `email-${user.email}`;
  return 'anonymous';
}

function _isGetRequest(config) {
  return (config?.method || 'get').toLowerCase() === 'get';
}

function _isMutationRequest(config) {
  const method = (config?.method || 'get').toLowerCase();
  return ['post', 'put', 'patch', 'delete'].includes(method);
}

function _isAuthRoute(url = '') {
  return url.includes('/auth/login') || url.includes('/auth/refresh');
}

const JWT_RESUME_BUFFER_SEC = 90;
const REFRESH_MAX_ATTEMPTS = 3;

function _parseJwtPayload(token) {
  try {
    const parts = String(token || '').split('.');
    if (parts.length !== 3) return null;
    const payload = parts[1];
    const base64 = payload.replace(/-/g, '+').replace(/_/g, '/');
    const padded = base64 + '='.repeat((4 - (base64.length % 4)) % 4);
    const json = decodeURIComponent(Array.prototype.map.call(atob(padded), (c) => (
      `%${(`00${c.charCodeAt(0).toString(16)}`).slice(-2)}`
    )).join(''));
    return JSON.parse(json);
  } catch {
    return null;
  }
}

function _isAccessTokenStale(token, bufferSec = JWT_RESUME_BUFFER_SEC) {
  const payload = _parseJwtPayload(token);
  const exp = Number(payload?.exp);
  if (!Number.isFinite(exp)) return false;
  const nowSec = Math.floor(Date.now() / 1000);
  return exp <= nowSec + bufferSec;
}

function _hasRefreshCookieHint() {
  if (typeof document === 'undefined') return false;
  return document.cookie.includes('ucp_refresh=');
}

// Stabilisation réseau:
// - production (Render/Vercel): marge plus large pour cold starts et charge variable
// - développement: valeurs plus serrées pour feedback rapide
const DEFAULT_REQUEST_TIMEOUT_MS = process.env.NODE_ENV === 'production' ? 20000 : 10000;
const AUTH_REQUEST_TIMEOUT_MS = process.env.NODE_ENV === 'production' ? 60000 : 25000;
/** Listes métier volumineuses (clients / interventions / rapports / sites / équipements). */
const HEAVY_LIST_TIMEOUT_MS = process.env.NODE_ENV === 'production' ? 60000 : 30000;
/** Import / export / template multi-entités — peut dépasser plusieurs minutes. */
const LONG_IMPORT_TIMEOUT_MS = 30 * 60 * 1000;

function _isLongRunningImportExportRoute(url = '') {
  return /\/import-export\/(import|export|template)\//i.test(String(url));
}

function _isHeavyListRoute(url = '') {
  const path = String(url || '').split('?')[0];
  return (
    /\/clients$/i.test(path)
    || /\/interventions$/i.test(path)
    || /\/intervention-reports$/i.test(path)
    || /\/sites$/i.test(path)
    || /\/equipements$/i.test(path)
  );
}

function _isGatewayUnavailableLike(error) {
  const status = Number(error?.response?.status || 0);
  if ([502, 503, 504].includes(status)) return true;
  const code = String(error?.code || '').toUpperCase();
  if (code === 'ECONNABORTED') return true;
  const msg = String(error?.message || '').toLowerCase();
  return msg.includes('timeout');
}

function _sleep(ms) {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

async function loginWithRetry(credentials) {
  const requestConfig = {
    withCredentials: true,
    timeout: AUTH_REQUEST_TIMEOUT_MS,
  };

  try {
    return await api.post('/auth/login', credentials, requestConfig);
  } catch (firstError) {
    // Robustesse prod: une relance unique couvre les réveils cold-start API.
    if (!_isGatewayUnavailableLike(firstError)) {
      throw firstError;
    }

    await _sleep(1500);
    return api.post('/auth/login', credentials, requestConfig);
  }
}

const RETRYABLE_HTTP_STATUS = new Set([502, 503, 504]);

function _isRetryableGetRequest(error, requestConfig) {
  if (!requestConfig) return false;
  const method = String(requestConfig.method || 'get').toLowerCase();
  if (method !== 'get') return false;
  if (_isAuthRoute(requestConfig.url || '')) return false;
  if ((requestConfig.url || '').includes('/stream')) return false;

  const status = Number(error?.response?.status || 0);
  const isHttpRetryable = RETRYABLE_HTTP_STATUS.has(status);
  const isNetworkTimeout = !error?.response && (
    String(error?.code || '').toUpperCase() === 'ECONNABORTED'
    || String(error?.message || '').toLowerCase().includes('timeout')
  );

  const retryCount = Number(requestConfig._retryCount || 0);
  if (retryCount >= 1) return false;

  return isHttpRetryable || isNetworkTimeout;
}

function _isEmptyEquipementsCachePayload(data) {
  const rows = data?.data;
  return Array.isArray(rows) && rows.length === 0;
}

function _isCacheableGet(config) {
  const url = config?.url || '';
  if (!_isGetRequest(config)) return false;
  if (_isAuthRoute(url)) return false;
  if (url.includes('/stream')) return false;
  return true;
}

function _serializeParams(params) {
  if (!params || typeof params !== 'object') return '';
  // Ignorer cache-busters pour partager la clé offline (GO_OFFLINE_DETAIL_ENTITIES).
  const keys = Object.keys(params)
    .filter((key) => key !== '_nc' && key !== '_cb' && key !== '_t')
    .sort();
  const normalized = {};
  keys.forEach((key) => {
    normalized[key] = params[key];
  });
  return JSON.stringify(normalized);
}

function _buildCacheKey(config) {
  const scope = _getOfflineScope();
  const baseUrl = config?.baseURL || API_BASE_URL;
  const url = config?.url || '';
  const params = _serializeParams(config?.params);
  return `${OFFLINE_CACHE_PREFIX}:${scope}:${baseUrl}:${url}:${params}`;
}

function _putMemoryCachedResponse(config, data) {
  if (!_isCacheableGet(config)) return;
  const key = _buildCacheKey(config);
  _memoryGetCache.set(key, { timestamp: Date.now(), data });
  if (_memoryGetCache.size <= MEMORY_GET_CACHE_MAX) return;
  let oldestKey = null;
  let oldestTs = Infinity;
  for (const [cacheKey, entry] of _memoryGetCache.entries()) {
    const ts = Number(entry?.timestamp) || 0;
    if (ts < oldestTs) {
      oldestTs = ts;
      oldestKey = cacheKey;
    }
  }
  if (oldestKey) _memoryGetCache.delete(oldestKey);
}

function _clearMemoryGetCache() {
  _memoryGetCache.clear();
}

function _setCachedResponse(config, data) {
  if (!_isCacheableGet(config)) return;
  const url = String(config?.url || '');
  const responseType = String(config?.responseType || '').toLowerCase();
  // GO_OFFLINE_MEDIA_API : binaires → Cache API dédié, pas JSON LS/IDB.
  if (responseType === 'blob' || responseType === 'arraybuffer') return;
  if (isInterventionReportImageContentPath(url)) return;
  if (url.includes('/equipements') && _isEmptyEquipementsCachePayload(data)) {
    return;
  }
  const key = _buildCacheKey(config);
  const serializedData = JSON.stringify(data);
  if (typeof serializedData !== 'string') {
    return;
  }
  const approxBytes = serializedData.length * 2;
  const timestamp = Date.now();
  const payload = {
    timestamp,
    data: JSON.parse(serializedData),
  };

  _putMemoryCachedResponse(config, payload.data);

  // GO_OFFLINE_FULL W1 : IDB pour entrées larges / volume ; LS pour hit sync rapide
  void putApiReadCacheEntry({
    key,
    scope: _getOfflineScope(),
    data: payload.data,
    timestamp
  });

  if (approxBytes > OFFLINE_CACHE_MAX_ENTRY_BYTES) {
    return;
  }

  _pruneOfflineCacheToLimits();
  const stored = safeLocalStorageSetItem(key, JSON.stringify(payload));
  if (!stored) {
    console.warn('⚠️ Cache hors-ligne LS indisponible (IDB peut rester actif)');
  }
}

function _getCachedResponse(config) {
  if (!_isCacheableGet(config)) return null;
  const key = _buildCacheKey(config);
  const fromMem = _memoryGetCache.get(key);
  if (fromMem && typeof fromMem === 'object' && ('data' in fromMem)) return fromMem;
  const raw = localStorage.getItem(key);
  if (!raw) return null;
  const parsed = _safeJsonParse(raw, null);
  if (!parsed || typeof parsed !== 'object' || !('data' in parsed)) return null;
  return parsed;
}

/** Alias lecture offline : chemins UI ≠ chemins warmup (GO_OFFLINE_READ_COMPLETE + DETAIL + PARAMS). */
function _offlineReadPathAliases(urlPath) {
  const path = String(urlPath || '').trim().replace(/\/+$/, '');
  if (!path) return [];
  const aliases = [];
  let m;
  if ((m = path.match(/^\/client\/rapports\/([^/]+)$/))) {
    aliases.push(`/intervention-reports/${m[1]}`);
  } else if ((m = path.match(/^\/intervenant\/rapports\/([^/]+)$/))) {
    aliases.push(`/intervention-reports/${m[1]}`);
  } else if ((m = path.match(/^\/intervention-reports\/([^/]+)$/))) {
    aliases.push(`/client/rapports/${m[1]}`, `/intervenant/rapports/${m[1]}`);
  }
  if ((m = path.match(/^\/client\/interventions\/([^/]+)$/))) {
    aliases.push(`/interventions/${m[1]}`, `/intervenant/interventions/${m[1]}`);
  } else if ((m = path.match(/^\/intervenant\/interventions\/([^/]+)$/))) {
    aliases.push(`/interventions/${m[1]}`, `/client/interventions/${m[1]}`);
  } else if ((m = path.match(/^\/interventions\/([^/]+)$/))) {
    aliases.push(`/intervenant/interventions/${m[1]}`, `/client/interventions/${m[1]}`);
  }
  if (path === '/intervenants') {
    aliases.push('/auth/intervenants', '/auth/superadmin/intervenants');
  } else if (path === '/auth/intervenants' || path === '/auth/superadmin/intervenants') {
    aliases.push('/intervenants');
  }
  if (path === '/organization-contact') {
    aliases.push('/public/organization-contact', '/support-contact');
  } else if (path === '/public/organization-contact') {
    aliases.push('/organization-contact', '/support-contact');
  } else if (path === '/support-contact') {
    aliases.push('/organization-contact', '/public/organization-contact');
  }
  return aliases;
}

/** Soft-hit path-only autorisé uniquement pour params « pagination / bust » (pas code_client, etc.). */
const OFFLINE_SOFT_HIT_PARAM_ALLOWLIST = new Set([
  'limit',
  'offset',
  'page',
  'pageSize',
  'per_page',
  'unreadLimit',
  'readLimit',
  'exclude_reads',
  'scope',
  'view',
  'slim',
  'from',
  'to',
  'date_from',
  'date_to',
  'fields',
  '_nc',
  '_cb',
  '_t'
]);

function _configHasRestrictiveOfflineParams(config) {
  const params = config?.params;
  if (params && typeof params === 'object') {
    for (const key of Object.keys(params)) {
      if (OFFLINE_SOFT_HIT_PARAM_ALLOWLIST.has(key)) continue;
      const value = params[key];
      if (value == null || value === '') continue;
      return true;
    }
  }
  const url = String(config?.url || '');
  const qIndex = url.indexOf('?');
  if (qIndex < 0) return false;
  try {
    const search = new URLSearchParams(url.slice(qIndex + 1));
    for (const key of search.keys()) {
      if (OFFLINE_SOFT_HIT_PARAM_ALLOWLIST.has(key)) continue;
      const value = search.get(key);
      if (value == null || value === '') continue;
      return true;
    }
  } catch {
    return true;
  }
  return false;
}

async function _getCachedResponseAsync(config) {
  const fromLs = _getCachedResponse(config);
  if (fromLs) return fromLs;
  if (!_isCacheableGet(config)) return null;
  const key = _buildCacheKey(config);
  const fromIdb = await getApiReadCacheEntry(key);
  if (fromIdb && ('data' in fromIdb)) return fromIdb;

  // GO_OFFLINE_READ_HEAVY : hors ligne, snapshot même path (params pagination seulement).
  // GO_OFFLINE_EQUIP_SOFT_HIT : jamais soft-hit si filtre métier (ex. code_client → 1395 fantômes).
  const offline = typeof navigator !== 'undefined' && !navigator.onLine;
  if (!offline) return null;
  if (_configHasRestrictiveOfflineParams(config)) return null;
  const urlPath = String(config?.url || '').split('?')[0];
  if (!urlPath) return null;
  const scope = _getOfflineScope();
  const soft = await findApiReadCacheByUrlPath(urlPath, { scope });
  if (soft && ('data' in soft)) return soft;
  // GO_OFFLINE_READ_COMPLETE : retomber sur alias de lecture (Client vs SA, /intervenants).
  for (const alias of _offlineReadPathAliases(urlPath)) {
    const hit = await findApiReadCacheByUrlPath(alias, { scope });
    if (hit && ('data' in hit)) return hit;
  }
  return null;
}

function _readOfflineQueue() {
  let raw = null;
  try {
    raw = localStorage.getItem(OFFLINE_QUEUE_KEY);
  } catch {
    raw = null;
  }
  return raw ? _safeJsonParse(raw, []) : [];
}

function _writeOfflineQueue(queue) {
  safeLocalStorageSetItem(OFFLINE_QUEUE_KEY, JSON.stringify(queue));
}

/** Purge file hors-ligne (évite boucle login PWA iOS après 401). */
export function clearOfflineQueueOnSessionEnd() {
  _writeOfflineQueue([]);
  clearAllOptimisticInterventionStatuses();
  void clearTerrainOutbox().catch(() => {});
  void clearApiReadCache().catch(() => {});
  _clearMemoryGetCache();
  try {
    localStorage.removeItem(OFFLINE_SNAPSHOT_META_KEY);
  } catch {
    // no-op
  }
}

/**
 * REGRESSION (02 juin 2026) — boucle login PWA (Chrome / iOS, icône écran d’accueil).
 * Ne jamais rejouer ucp_api_queue_v1 sans Bearer ; purger la file sur perte de session.
 */
/** Replay file uniquement si un access token est présent (pas seul cookie refresh sur /). */
function _canReplayOfflineQueue() {
  const hdr = api.defaults.headers?.common?.Authorization || '';
  if (String(hdr).startsWith('Bearer ')) return true;
  const token = _safeGetStorageItem('token');
  return Boolean(token && String(token).trim());
}

function _redirectToLoginIfNeeded() {
  if (typeof window !== 'undefined' && isOnPublicAuthRoute(window.location.pathname)) return;
  window.location.assign('/');
}

/** Lot 5 + 6 — ne pas rejouer la file offline depuis login / routes publiques (évite boucle 401). */
function _shouldScheduleOfflineFlush(config) {
  const pathname = typeof window !== 'undefined' ? window.location.pathname : '/';
  return shouldScheduleOfflineFlushForRoute({
    hasBearer: _canReplayOfflineQueue(),
    pathname,
    url: config?.url || '',
  });
}

function _forceAuthLogoutAndRedirect() {
  localStorage.removeItem('token');
  localStorage.removeItem('user');
  try { sessionStorage.removeItem('token'); } catch (_) {}
  try { sessionStorage.removeItem('user'); } catch (_) {}
  setAuthToken(null);
  clearOfflineQueueOnSessionEnd();
  _redirectToLoginIfNeeded();
}

/** Mutations irréversibles lourdes : PDF / imports — jamais en file auto (OOM Render / Puppeteer).
 * GO_OFFLINE_MUTATIONS_PARITY : POST /interventions (création) est désormais fileable + sync. */
function _isCriticalMutationDeniedOfflineQueue(url) {
  const u = String(url || '');
  return (
    /\/activity-reports\/generate\b/i.test(u) ||
    /\/generate-pdf\b/i.test(u) ||
    /\/import-export\/import\//i.test(u)
  );
}

/** Bruit présence / typing — ne jamais outbox (GO_OUTBOX_NOISE). */
function _isPresenceNoiseOfflineQueue(url) {
  const u = String(url || '');
  return (
    /\/presence\/heartbeat\b/i.test(u) ||
    /\/presence\/offline\b/i.test(u) ||
    /\/presence\/batch\b/i.test(u) ||
    /\/typing\b/i.test(u)
  );
}

function _isOfflineQueueDenied(url) {
  return _isCriticalMutationDeniedOfflineQueue(url) || _isPresenceNoiseOfflineQueue(url);
}

/** Retire PDF / présence déjà en file LS. */
function _purgeDeniedMutationsFromOfflineQueue() {
  const queue = _readOfflineQueue();
  if (!Array.isArray(queue) || queue.length === 0) return 0;
  const kept = queue.filter((item) => !_isOfflineQueueDenied(item?.url));
  const removed = queue.length - kept.length;
  if (removed > 0) {
    _writeOfflineQueue(kept);
  }
  return removed;
}

let _noisePurgeInFlight = null;

/** Purge one-shot ops présence (IDB + LS) — GO_OUTBOX_NOISE. */
export async function purgeOfflineNoiseOps() {
  if (_noisePurgeInFlight) return _noisePurgeInFlight;
  _noisePurgeInFlight = (async () => {
    const lsRemoved = _purgeDeniedMutationsFromOfflineQueue();
    let idbRemoved = 0;
    try {
      const scope = _getOfflineScope();
      const rows = await listTerrainOps({ scope });
      for (const op of rows) {
        if (_isPresenceNoiseOfflineQueue(op?.url) || _isCriticalMutationDeniedOfflineQueue(op?.url)) {
          await removeTerrainOp(op.clientOpId);
          idbRemoved += 1;
        }
      }
    } catch (err) {
      console.warn('[UCP] purge noise outbox IDB failed:', err?.message || err);
    }
    if (lsRemoved + idbRemoved > 0) {
      _dispatchOfflineEvent('ucp-offline-sync-dropped', {
        reason: 'noise-purge',
        lsRemoved,
        idbRemoved
      });
    }
    return { lsRemoved, idbRemoved };
  })();
  try {
    return await _noisePurgeInFlight;
  } finally {
    _noisePurgeInFlight = null;
  }
}

let _lastOfflineFlushAt = 0;
const OFFLINE_FLUSH_MIN_INTERVAL_MS = 4000;

function _shouldDeferOfflineFlush() {
  return Date.now() - _lastOfflineFlushAt < OFFLINE_FLUSH_MIN_INTERVAL_MS;
}

function _scheduleOfflineFlush({ force = false } = {}) {
  if (typeof window === 'undefined') return;
  if (!_canReplayOfflineQueue()) return;
  if (!force && _shouldDeferOfflineFlush()) return;
  if (typeof navigator !== 'undefined' && !navigator.onLine) return;
  _lastOfflineFlushAt = Date.now();
  _flushOfflineQueue();
}

/** Sync manuelle (UI outbox) — GO_OFFLINE_FULL W1 */
export async function requestOfflineFlush({ force = true } = {}) {
  if (typeof window === 'undefined') return getOfflinePendingSummary();
  if (!_canReplayOfflineQueue()) return getOfflinePendingSummary();
  if (typeof navigator !== 'undefined' && !navigator.onLine) return getOfflinePendingSummary();
  if (!force && _shouldDeferOfflineFlush()) return getOfflinePendingSummary();
  _lastOfflineFlushAt = Date.now();
  await _flushOfflineQueue();
  return getOfflinePendingSummary();
}

/** Compteur actions en attente (IDB + LS legacy uniquement, sans double-comptage miroir). */
export async function getOfflinePendingSummary() {
  try {
    await purgeOfflineNoiseOps();
  } catch {
    // no-op
  }
  const scope = _getOfflineScope();
  let idbPending = 0;
  try {
    idbPending = await countPendingTerrainOps(scope);
  } catch {
    idbPending = 0;
  }
  const lsQueue = _readOfflineQueue();
  // Miroir IDB porte terrainOpId → ne pas recompter. Legacy = sans terrainOpId.
  const legacyOnly = Array.isArray(lsQueue)
    ? lsQueue.filter((item) => {
      if (!item || (item.scope && item.scope !== scope)) return false;
      if (item.terrainOpId) return false;
      if (_isOfflineQueueDenied(item.url)) return false;
      return true;
    }).length
    : 0;
  return {
    pending: Math.max(idbPending, 0) + legacyOnly,
    idbPending,
    legacyOnly,
    online: typeof navigator === 'undefined' ? true : navigator.onLine,
    scope
  };
}

/** Liste ops pending pour panel W2 (sans payloads). */
export async function getOfflinePendingOps({ limit = 40 } = {}) {
  try {
    await purgeOfflineNoiseOps();
  } catch {
    // no-op
  }
  const scope = _getOfflineScope();
  let rows = [];
  try {
    rows = await listTerrainOps({ status: 'pending', scope });
  } catch {
    rows = [];
  }
  return rows.slice(0, Math.max(1, Number(limit) || 40)).map((op) => ({
    clientOpId: op.clientOpId,
    method: op.method,
    url: op.url,
    createdAt: op.createdAt,
    attempts: op.attempts || 0,
    lastError: op.lastError || null,
    opType: op.opType || 'http'
  }));
}

function _canQueueMutation(config, _error) {
  if (!_isMutationRequest(config)) return false;
  if (config?._skipOfflineQueue) return false;
  const url = config?.url || '';
  if (_isAuthRoute(url)) return false;
  if (_isOfflineQueueDenied(url)) return false;
  return true;
}

function _classifyTerrainOpType(url) {
  if (isWorkflowMutationUrl(url)) return 'workflow_iv';
  if (isIntervenantReportMutationUrl(url)) return 'report_iv';
  return 'http';
}

function _extractInterventionIdFromConfigData(data) {
  try {
    if (typeof FormData !== 'undefined' && data instanceof FormData) {
      return data.get('intervention_id') || data.get('interventionId') || null;
    }
  } catch {
    // no-op
  }
  if (data && typeof data === 'object') {
    return data.intervention_id || data.interventionId || null;
  }
  return null;
}

function _extractReportIdFromResponseData(payload) {
  return (
    payload?.rapport?.id ||
    payload?.report?.id ||
    payload?.data?.id ||
    payload?.id ||
    null
  );
}

/**
 * Enfile dans IndexedDB (source de vérité) + miroir localStorage JSON si non-multipart.
 */
async function _queueMutation(config, error) {
  if (!_canQueueMutation(config, error)) return null;

  const method = (config?.method || 'post').toLowerCase();
  const url = config?.url || '';
  const isMultipart = typeof FormData !== 'undefined' && config?.data instanceof FormData;
  const contentType = config?.headers?.['Content-Type'] || config?.headers?.['content-type'] || '';
  const looksMultipart = isMultipart || String(contentType).includes('multipart/form-data');
  const workflowParsed = parseInterventionIdFromWorkflowUrl(url);
  const reportInterventionId = isIntervenantReportMutationUrl(url)
    ? _extractInterventionIdFromConfigData(config?.data)
    : null;
  const entityId = workflowParsed?.id || reportInterventionId || null;

  let terrainOp = null;
  try {
    terrainOp = await enqueueTerrainOp({
      scope: _getOfflineScope(),
      method,
      url,
      data: config?.data,
      params: config?.params || null,
      headers: { ...(config?.headers || {}) },
      opType: _classifyTerrainOpType(url),
      entityType: entityId ? 'intervention' : null,
      entityId: entityId ? String(entityId) : null,
    });
  } catch (idbErr) {
    console.warn('[UCP] Outbox IDB indisponible, fallback localStorage:', idbErr?.message || idbErr);
  }

  // Miroir LS uniquement pour JSON (compat flush legacy / diagnostic)
  let legacyId = terrainOp?.clientOpId || `${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
  if (!looksMultipart) {
    const queue = _readOfflineQueue();
    const item = {
      id: legacyId,
      createdAt: Date.now(),
      scope: _getOfflineScope(),
      method,
      url,
      data: config?.data,
      params: config?.params || null,
      headers: { ...(config?.headers || {}) },
      terrainOpId: terrainOp?.clientOpId || null,
    };
    queue.push(item);
    _writeOfflineQueue(queue.slice(-OFFLINE_QUEUE_MAX));
  }

  rememberOptimisticFromQueuedConfig(config);

  return {
    id: legacyId,
    clientOpId: terrainOp?.clientOpId || legacyId,
    url,
    method,
  };
}

async function _enqueueOfflineMutationResponse(originalRequest, error) {
  const queuedItem = await _queueMutation(originalRequest, error);
  if (!queuedItem) {
    return Promise.reject(error);
  }

  _dispatchOfflineEvent('ucp-offline-queued', {
    id: queuedItem.id,
    clientOpId: queuedItem.clientOpId,
    url: queuedItem.url,
    method: queuedItem.method,
  });

  const responseData = buildOfflineQueuedResponseData(originalRequest, queuedItem.clientOpId);

  return {
    data: responseData,
    status: 202,
    statusText: 'Accepted (offline-queue)',
    headers: {},
    config: originalRequest,
    request: null,
    fromOfflineQueue: true,
  };
}

function _dispatchOfflineEvent(name, detail = {}) {
  if (typeof window === 'undefined') return;
  window.dispatchEvent(new CustomEvent(name, { detail }));
}

const OFFLINE_WARMUP_ENDPOINTS = {
  common: [
    { url: '/support-contact' },
    { url: '/public/organization-contact' },
    { url: '/organization-contact' },
    { url: '/public-pages-settings' },
    { url: '/me/preferences' },
    { url: '/auth/dashboard/stats' },
    { url: '/dashboard/activities', params: { limit: 50, exclude_reads: 'true' } },
    { url: '/intervention-types' },
    { url: '/clients', params: { limit: 10000 } },
    { url: '/sites', params: { limit: 5000 } },
    { url: '/equipements', params: { limit: 5000 } },
    { url: '/interventions' },
    { url: '/intervention-reports' },
    { url: '/intervention-reports/stats' },
    { url: '/messaging/threads' },
    { url: '/messaging/unread-count' }
  ],
  superadmin: [
    { url: '/parameters' },
    { url: '/admins/profile' },
    { url: '/auth/superadmin/admins' },
    { url: '/auth/superadmin/intervenants' },
    { url: '/auth/intervenants' },
    { url: '/disponibilites/calendar' }
  ],
  admin: [
    { url: '/auth/intervenants' },
    { url: '/admins/profile' },
    { url: '/disponibilites/calendar' }
  ],
  intervenant: [
    { url: '/intervenant/dashboard' },
    { url: '/intervenant/interventions' },
    { url: '/intervenant/rapports' },
    { url: '/intervenant/disponibilites' },
    { url: '/intervenant/profil' }
  ],
  client: [
    { url: '/client/dashboard' },
    { url: '/client/interventions' },
    { url: '/client/equipements' },
    { url: '/client/rapports' },
    { url: '/client/profil' },
    { url: '/sites' }
  ]
};

/** Sets warmup prod — GO_OFFLINE_FULL W1 + PARAMS + MSG + pages SA. */
const OFFLINE_WARMUP_ENDPOINTS_TERRAIN = {
  common: [
    { url: '/support-contact' },
    { url: '/public/organization-contact' },
    { url: '/organization-contact' },
    { url: '/public-pages-settings' },
    { url: '/me/preferences' },
    { url: '/intervention-types' },
    { url: '/notifications/overview', params: { unreadLimit: 40, readLimit: 40 } },
    { url: '/messaging/threads' },
    { url: '/messaging/unread-count' }
  ],
  superadmin: [
    { url: '/auth/dashboard/stats' },
    { url: '/admins/profile' },
    { url: '/parameters' },
    { url: '/interventions' },
    { url: '/disponibilites/calendar' },
    { url: '/auth/superadmin/admins' },
    { url: '/auth/superadmin/intervenants' },
    { url: '/auth/intervenants' },
    { url: '/clients', params: { limit: 10000 } },
    { url: '/sites', params: { limit: 5000 } },
    { url: '/equipements', params: { limit: 5000 } },
    { url: '/intervention-reports' }
  ],
  admin: [
    { url: '/auth/dashboard/stats' },
    { url: '/admins/profile' },
    { url: '/interventions' },
    { url: '/disponibilites/calendar' },
    { url: '/auth/intervenants' },
    { url: '/clients', params: { limit: 10000 } },
    { url: '/sites', params: { limit: 5000 } },
    { url: '/equipements', params: { limit: 5000 } },
    { url: '/intervention-reports' }
  ],
  intervenant: [
    { url: '/intervenant/dashboard' },
    { url: '/intervenant/interventions' },
    { url: '/intervenant/rapports' },
    { url: '/intervenant/disponibilites' },
    { url: '/intervenant/profil' }
  ],
  client: [
    { url: '/client/dashboard' },
    { url: '/client/interventions' },
    { url: '/client/equipements' },
    { url: '/client/rapports' },
    { url: '/client/profil' },
    { url: '/sites' }
  ]
};

function _isOfflineWarmupEnabled() {
  const flag = String(process.env.REACT_APP_OFFLINE_WARMUP || '').trim().toLowerCase();
  if (flag === '0' || flag === 'false' || flag === 'off') return false;
  return true;
}

function _getOfflineWarmupMode() {
  const mode = String(process.env.REACT_APP_OFFLINE_WARMUP_MODE || '').trim().toLowerCase();
  if (mode === 'full' || mode === 'terrain') return mode;
  return process.env.NODE_ENV === 'production' ? 'terrain' : 'full';
}

function _writeOfflineSnapshotMeta(meta) {
  try {
    localStorage.setItem(OFFLINE_SNAPSHOT_META_KEY, JSON.stringify(meta));
  } catch {
    // quota — ignore
  }
}

export function isOfflineWarmupEnabled() {
  return _isOfflineWarmupEnabled();
}

export function getOfflineWarmupDelayMs() {
  const n = Number(process.env.REACT_APP_OFFLINE_WARMUP_DELAY_MS);
  if (Number.isFinite(n) && n >= 0) return n;
  // PERF_PLANNING Lot D : laisser le planning peindre avant phase 1 listes.
  return process.env.NODE_ENV === 'production' ? 30000 : 0;
}

export function getOfflineSnapshotMeta() {
  try {
    const raw = localStorage.getItem(OFFLINE_SNAPSHOT_META_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

/** Lecture cache GET synchrone (RAM puis LS) — 1er paint page Clients sans spinner. */
export function peekCachedApiGetSync(url, params) {
  return _getCachedResponse({
    method: 'get',
    url,
    params: params || undefined,
  });
}

/** Lecture cache GET (RAM/LS/IDB) sans réseau — paint immédiat page Clients. */
export async function peekCachedApiGet(url, params) {
  return _getCachedResponseAsync({
    method: 'get',
    url,
    params: params || undefined,
  });
}

/** PERF_PLANNING 27AOUT2026 — dédup in-flight des GET listes lourdes (planning + avatars). */
const _inflightGetPromises = new Map();

function _buildInflightGetKey(url, params) {
  return `GET:${String(url || '')}:${_serializeParams(params)}`;
}

export function sharedApiGet(url, config = {}) {
  const key = _buildInflightGetKey(url, config?.params);
  const existing = _inflightGetPromises.get(key);
  if (existing) return existing;
  const promise = api.get(url, config).finally(() => {
    if (_inflightGetPromises.get(key) === promise) {
      _inflightGetPromises.delete(key);
    }
  });
  _inflightGetPromises.set(key, promise);
  return promise;
}

function _dedupeWarmupEntries(entries) {
  const seen = new Set();
  return entries.filter((entry) => {
    const key = `${entry.url}::${_serializeParams(entry.params)}`;
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });
}

function _isDeferredHeavyWarmupEntry(entry) {
  const url = String(entry?.url || '');
  if (url === '/sites' || url === '/equipements') return true;
  return false;
}

/** PERF_PLANNING Lot D 27AOUT2026 — caps / délais phase 2 (détails hors boot planning). */
function _envNonNegInt(name, fallback) {
  const n = Number(process.env[name]);
  return Number.isFinite(n) && n >= 0 ? n : fallback;
}

function _envFlagDefault(name, defaultOn) {
  const raw = String(process.env[name] ?? '').trim().toLowerCase();
  if (raw === '0' || raw === 'false' || raw === 'off') return false;
  if (raw === '1' || raw === 'true' || raw === 'on') return true;
  return defaultOn;
}

function _warmupLotDLimits() {
  const isProd = process.env.NODE_ENV === 'production';
  return {
    reportDetails: _envNonNegInt('REACT_APP_OFFLINE_WARMUP_REPORT_DETAILS', isProd ? 5 : 10),
    ivDetails: _envNonNegInt('REACT_APP_OFFLINE_WARMUP_IV_DETAILS', isProd ? 5 : 10),
    entityDetails: _envNonNegInt('REACT_APP_OFFLINE_WARMUP_ENTITY_DETAILS', isProd ? 5 : 10),
    mediaTotal: _envNonNegInt('REACT_APP_OFFLINE_WARMUP_MEDIA_TOTAL', isProd ? 10 : 40),
    mediaPerReport: _envNonNegInt('REACT_APP_OFFLINE_WARMUP_MEDIA_PER_REPORT', isProd ? 2 : 5),
    msgThreads: _envNonNegInt('REACT_APP_OFFLINE_WARMUP_MSG_THREADS', isProd ? 5 : 15),
    docClients: _envNonNegInt('REACT_APP_OFFLINE_WARMUP_CLIENT_DOCS_CLIENTS', isProd ? 5 : 15),
    docsPerClient: _envNonNegInt('REACT_APP_OFFLINE_WARMUP_CLIENT_DOCS_PER_CLIENT', isProd ? 2 : 5),
    docsMediaCap: _envNonNegInt('REACT_APP_OFFLINE_WARMUP_CLIENT_DOCS_MEDIA_TOTAL', isProd ? 15 : 60),
    phase2DelayMs: _envNonNegInt('REACT_APP_OFFLINE_WARMUP_PHASE2_DELAY_MS', isProd ? 60000 : 2500),
    yieldMs: _envNonNegInt('REACT_APP_OFFLINE_WARMUP_YIELD_MS', isProd ? 250 : 40),
    warmMediaBinaries: _envFlagDefault('REACT_APP_OFFLINE_WARMUP_MEDIA', !isProd),
    warmEquipLinks: _envFlagDefault('REACT_APP_OFFLINE_WARMUP_EQUIP_LINKS', !isProd),
    warmClientDocs: _envFlagDefault('REACT_APP_OFFLINE_WARMUP_CLIENT_DOCS', !isProd),
  };
}

function _warmupShouldPauseDetails() {
  if (typeof navigator !== 'undefined' && !navigator.onLine) return true;
  if (typeof document !== 'undefined' && document.visibilityState === 'hidden') return true;
  return false;
}

async function _warmupYield(ms) {
  const wait = Number(ms) || 0;
  if (wait <= 0) return;
  await new Promise((resolve) => setTimeout(resolve, wait));
}

let _warmupGeneration = 0;
let _warmupPhase2Timer = null;

function _clearWarmupPhase2Timer() {
  if (_warmupPhase2Timer != null) {
    clearTimeout(_warmupPhase2Timer);
    _warmupPhase2Timer = null;
  }
}

export async function warmupOfflineData(role) {
  if (!_isOfflineWarmupEnabled()) return { started: false, reason: 'disabled' };
  if (_isWarmingOfflineCache) return { started: false, reason: 'already-running' };
  if (typeof navigator !== 'undefined' && !navigator.onLine) {
    return { started: false, reason: 'offline' };
  }
  if (!_canReplayOfflineQueue()) {
    return { started: false, reason: 'no-auth' };
  }

  const roleKey = (role || '').toLowerCase();
  const mode = _getOfflineWarmupMode();
  const limits = _warmupLotDLimits();
  const catalog = mode === 'terrain' ? OFFLINE_WARMUP_ENDPOINTS_TERRAIN : OFFLINE_WARMUP_ENDPOINTS;
  const commonEntries =
    roleKey === 'intervenant' && mode === 'full'
      ? catalog.common.filter((e) => e.url !== '/clients')
      : catalog.common;
  const allEntries = _dedupeWarmupEntries([
    ...commonEntries,
    ...(catalog[roleKey] || [])
  ]);
  // PERF_PLANNING : sites/équipements 5k + détails → phase 2 uniquement.
  const deferredHeavy = allEntries.filter(_isDeferredHeavyWarmupEntry);
  const entries = allEntries.filter((e) => !_isDeferredHeavyWarmupEntry(e));

  const gen = ++_warmupGeneration;
  _clearWarmupPhase2Timer();
  _isWarmingOfflineCache = true;

  let successCount = 0;
  let failedCount = 0;
  const warmedUrls = [];
  let reportsListPayload = null;
  let intervenantReportsPayload = null;
  let clientReportsPayload = null;
  let interventionsListPayload = null;
  let intervenantInterventionsPayload = null;
  let clientInterventionsPayload = null;
  let messagingThreadsPayload = null;

  const warmGet = async (path, timeoutMs = 12000, params = undefined) => {
    try {
      await api.get(path, { params, _skipOfflineQueue: true, timeout: timeoutMs });
      successCount += 1;
      warmedUrls.push(path);
      return true;
    } catch {
      failedCount += 1;
      return false;
    }
  };

  try {
    // ——— PHASE 1 : listes légères (cache soft-paint planning / shell) ———
    for (const entry of entries) {
      if (gen !== _warmupGeneration) {
        _isWarmingOfflineCache = false;
        return { started: false, reason: 'superseded' };
      }
      try {
        const response = await api.get(entry.url, {
          params: entry.params,
          _skipOfflineQueue: true,
          timeout: _isHeavyListRoute(entry.url) ? HEAVY_LIST_TIMEOUT_MS : 12000,
        });
        successCount += 1;
        warmedUrls.push(entry.url);
        if (entry.url === '/intervention-reports') reportsListPayload = response?.data;
        if (entry.url === '/intervenant/rapports') intervenantReportsPayload = response?.data;
        if (entry.url === '/client/rapports') clientReportsPayload = response?.data;
        if (entry.url === '/interventions') interventionsListPayload = response?.data;
        if (entry.url === '/intervenant/interventions') intervenantInterventionsPayload = response?.data;
        if (entry.url === '/client/interventions') clientInterventionsPayload = response?.data;
        if (entry.url === '/messaging/threads') messagingThreadsPayload = response?.data;
      } catch {
        failedCount += 1;
      }
    }

    const phase1Snapshot = {
      role: roleKey || 'unknown',
      mode,
      phase: 1,
      successCount,
      failedCount,
      total: entries.length,
      urls: [...warmedUrls],
      phase2ScheduledInMs: limits.phase2DelayMs,
      timestamp: Date.now()
    };
    _writeOfflineSnapshotMeta(phase1Snapshot);
    _dispatchOfflineEvent('ucp-offline-warmup-phase1-complete', phase1Snapshot);

    // Libère le verrou phase 1 ; phase 2 reprend le flag via gen.
    _isWarmingOfflineCache = false;

    const phase2Payloads = {
      reportsListPayload,
      intervenantReportsPayload,
      clientReportsPayload,
      interventionsListPayload,
      intervenantInterventionsPayload,
      clientInterventionsPayload,
      messagingThreadsPayload,
      deferredHeavy,
      roleKey,
      mode,
      limits,
      gen,
      successCount,
      failedCount,
      warmedUrls: [...warmedUrls]
    };

    _warmupPhase2Timer = setTimeout(() => {
      _warmupPhase2Timer = null;
      void _runWarmupPhase2Details(phase2Payloads);
    }, limits.phase2DelayMs);

    return {
      started: true,
      ...phase1Snapshot
    };
  } catch (err) {
    _clearWarmupPhase2Timer();
    _isWarmingOfflineCache = false;
    throw err;
  }
}

async function _runWarmupPhase2Details(ctx) {
  const {
    roleKey,
    mode,
    limits,
    gen,
    deferredHeavy
  } = ctx;
  if (gen !== _warmupGeneration) return;
  if (_warmupShouldPauseDetails()) {
    // Replanifier plus tard si onglet caché / offline.
    _warmupPhase2Timer = setTimeout(() => {
      _warmupPhase2Timer = null;
      void _runWarmupPhase2Details(ctx);
    }, Math.max(limits.phase2DelayMs, 15000));
    return;
  }
  if (_isWarmingOfflineCache) return;
  _isWarmingOfflineCache = true;

  let successCount = Number(ctx.successCount) || 0;
  let failedCount = Number(ctx.failedCount) || 0;
  const warmedUrls = Array.isArray(ctx.warmedUrls) ? [...ctx.warmedUrls] : [];

  const warmGet = async (path, timeoutMs = 12000, params = undefined) => {
    try {
      await api.get(path, { params, _skipOfflineQueue: true, timeout: timeoutMs });
      successCount += 1;
      warmedUrls.push(path);
      return true;
    } catch {
      failedCount += 1;
      return false;
    }
  };

  try {
    const extractReportRows = (payload) => {
      if (!payload) return [];
      if (Array.isArray(payload)) return payload;
      return payload.reports
        || payload.interventionReports
        || payload.rapports
        || payload.data
        || [];
    };
    const reportRows = [
      ...extractReportRows(ctx.reportsListPayload),
      ...extractReportRows(ctx.intervenantReportsPayload),
      ...extractReportRows(ctx.clientReportsPayload)
    ].slice(0, limits.reportDetails);

    const seenReportIds = new Set();
    const seenInterventionIds = new Set();
    let mediaWarmedCount = 0;
    const reportDetailPathForRole = (reportId) => {
      if (roleKey === 'intervenant') return `/intervenant/rapports/${reportId}`;
      if (roleKey === 'client') return `/client/rapports/${reportId}`;
      return `/intervention-reports/${reportId}`;
    };

    for (const row of reportRows) {
      if (gen !== _warmupGeneration) return;
      if (_warmupShouldPauseDetails()) break;
      const reportId = row?.id;
      if (reportId == null || seenReportIds.has(String(reportId))) continue;
      seenReportIds.add(String(reportId));
      await warmGet(reportDetailPathForRole(reportId), HEAVY_LIST_TIMEOUT_MS);
      await _warmupYield(limits.yieldMs);
      await warmGet(`/intervention-reports/${reportId}/checks`);
      await _warmupYield(limits.yieldMs);
      let imagesPayload = null;
      try {
        const imagesResp = await api.get(`/intervention-reports/${reportId}/images`, {
          _skipOfflineQueue: true,
          timeout: 12000
        });
        successCount += 1;
        warmedUrls.push(`/intervention-reports/${reportId}/images`);
        imagesPayload = imagesResp?.data;
      } catch {
        failedCount += 1;
      }
      await _warmupYield(limits.yieldMs);

      if (limits.warmMediaBinaries && imagesPayload && mediaWarmedCount < limits.mediaTotal) {
        const imageRows = extractReportImageRows(imagesPayload).slice(0, limits.mediaPerReport);
        for (const imageRow of imageRows) {
          if (mediaWarmedCount >= limits.mediaTotal) break;
          const imageId = imageRow?.id;
          if (imageId == null) continue;
          const mediaPath = `/api/intervention-reports/${reportId}/images/${imageId}/content`;
          const ok = await warmApiMediaUrl(mediaPath);
          if (ok) {
            mediaWarmedCount += 1;
            warmedUrls.push(mediaPath);
          }
          await _warmupYield(limits.yieldMs);
        }
      }

      if (limits.warmEquipLinks) {
        const interventionId = row?.intervention_id || row?.interventionId;
        if (interventionId != null && !seenInterventionIds.has(String(interventionId))) {
          seenInterventionIds.add(String(interventionId));
          await warmGet(`/interventions/${interventionId}/equipements`, 12000, { scope: 'explicit' });
          await _warmupYield(limits.yieldMs);
        }
      }
    }

    const extractIvRows = (payload) => {
      if (!payload) return [];
      if (Array.isArray(payload)) return payload;
      return payload.interventions
        || payload.data
        || payload.items
        || [];
    };
    const ivRowsFromLists = [
      ...extractIvRows(ctx.interventionsListPayload),
      ...extractIvRows(ctx.intervenantInterventionsPayload),
      ...extractIvRows(ctx.clientInterventionsPayload)
    ];
    const ivIdCandidates = [];
    const pushIvId = (id) => {
      if (id == null) return;
      const key = String(id);
      if (ivIdCandidates.some((x) => String(x) === key)) return;
      ivIdCandidates.push(id);
    };
    for (const id of seenInterventionIds) pushIvId(id);
    for (const row of ivRowsFromLists) pushIvId(row?.id);
    const ivIds = ivIdCandidates.slice(0, limits.ivDetails);

    const ivDetailPathForRole = (ivId) => {
      if (roleKey === 'intervenant') return `/intervenant/interventions/${ivId}`;
      if (roleKey === 'client') return `/client/interventions/${ivId}`;
      return `/interventions/${ivId}`;
    };
    const seenIvDetailIds = new Set();
    const seenClientIds = new Set();
    const seenSiteIds = new Set();
    const seenEquipementIds = new Set();
    const pushEntityId = (set, id) => {
      if (id == null || id === '') return;
      set.add(String(id));
    };
    for (const row of [...ivRowsFromLists, ...reportRows]) {
      pushEntityId(seenClientIds, row?.client_id || row?.clientId);
      pushEntityId(seenSiteIds, row?.site_id || row?.siteId);
      pushEntityId(seenEquipementIds, row?.equipement_id || row?.equipementId);
    }

    for (const ivId of ivIds) {
      if (gen !== _warmupGeneration) return;
      if (_warmupShouldPauseDetails()) break;
      const key = String(ivId);
      if (seenIvDetailIds.has(key)) continue;
      seenIvDetailIds.add(key);
      await warmGet(ivDetailPathForRole(ivId), HEAVY_LIST_TIMEOUT_MS);
      await _warmupYield(limits.yieldMs);
      if (roleKey === 'client') {
        await warmGet(`/client/interventions/${ivId}/rapport`);
        await _warmupYield(limits.yieldMs);
      }
      if (limits.warmEquipLinks && !seenInterventionIds.has(key)) {
        seenInterventionIds.add(key);
        await warmGet(`/interventions/${ivId}/equipements`, 12000, { scope: 'explicit' });
        await _warmupYield(limits.yieldMs);
      }
    }

    let entityDetailsWarmed = 0;
    if (roleKey === 'admin' || roleKey === 'superadmin') {
      for (const clientId of [...seenClientIds].slice(0, limits.entityDetails)) {
        if (gen !== _warmupGeneration) return;
        if (_warmupShouldPauseDetails()) break;
        if (await warmGet(`/clients/${clientId}`)) entityDetailsWarmed += 1;
        await _warmupYield(limits.yieldMs);
      }
    }
    for (const siteId of [...seenSiteIds].slice(0, limits.entityDetails)) {
      if (gen !== _warmupGeneration) return;
      if (_warmupShouldPauseDetails()) break;
      if (await warmGet(`/sites/${siteId}`)) entityDetailsWarmed += 1;
      await _warmupYield(limits.yieldMs);
    }
    for (const equipementId of [...seenEquipementIds].slice(0, limits.entityDetails)) {
      if (gen !== _warmupGeneration) return;
      if (_warmupShouldPauseDetails()) break;
      if (await warmGet(`/equipements/${equipementId}`)) entityDetailsWarmed += 1;
      await _warmupYield(limits.yieldMs);
    }

    let clientDocsListsWarmed = 0;
    let clientDocsMediaWarmed = 0;
    if (
      limits.warmClientDocs
      && (roleKey === 'superadmin' || roleKey === 'admin')
    ) {
      for (const clientId of [...seenClientIds].slice(0, limits.docClients)) {
        if (gen !== _warmupGeneration) return;
        if (_warmupShouldPauseDetails()) break;
        try {
          const docsResp = await api.get(`/clients/${clientId}/documents`, {
            _skipOfflineQueue: true,
            timeout: 12000
          });
          successCount += 1;
          warmedUrls.push(`/clients/${clientId}/documents`);
          clientDocsListsWarmed += 1;
          const docs = extractClientDocumentRows(docsResp?.data).slice(0, limits.docsPerClient);
          for (const doc of docs) {
            if (clientDocsMediaWarmed >= limits.docsMediaCap) break;
            const filePath = doc?.file_path || doc?.filePath;
            if (!filePath) continue;
            const ok = await warmUploadsUrl(filePath);
            if (ok) {
              clientDocsMediaWarmed += 1;
              warmedUrls.push(String(filePath));
            }
            await _warmupYield(limits.yieldMs);
          }
        } catch {
          failedCount += 1;
        }
        await _warmupYield(limits.yieldMs);
      }
    }

    let messagingThreadsWarmed = 0;
    let messagingMessagesWarmed = 0;
    const threads = Array.isArray(ctx.messagingThreadsPayload)
      ? ctx.messagingThreadsPayload
      : (ctx.messagingThreadsPayload?.threads || ctx.messagingThreadsPayload?.data || []);
    for (const thread of threads.slice(0, limits.msgThreads)) {
      if (gen !== _warmupGeneration) return;
      if (_warmupShouldPauseDetails()) break;
      const threadId = thread?.id;
      if (threadId == null) continue;
      messagingThreadsWarmed += 1;
      try {
        await api.get(`/messaging/threads/${threadId}/messages`, {
          params: { limit: 80 },
          _skipOfflineQueue: true,
          timeout: 12000
        });
        successCount += 1;
        warmedUrls.push(`/messaging/threads/${threadId}/messages`);
        messagingMessagesWarmed += 1;
      } catch {
        failedCount += 1;
      }
      await _warmupYield(limits.yieldMs);
    }

    let deferredHeavyWarmed = 0;
    for (const entry of deferredHeavy || []) {
      if (gen !== _warmupGeneration) return;
      if (_warmupShouldPauseDetails()) break;
      try {
        await api.get(entry.url, {
          params: entry.params,
          _skipOfflineQueue: true,
          timeout: _isHeavyListRoute(entry.url) ? HEAVY_LIST_TIMEOUT_MS : 12000,
        });
        successCount += 1;
        warmedUrls.push(entry.url);
        deferredHeavyWarmed += 1;
      } catch {
        failedCount += 1;
      }
      await _warmupYield(limits.yieldMs);
    }

    if (gen !== _warmupGeneration) return;

    const snapshot = {
      role: roleKey || 'unknown',
      mode,
      phase: 2,
      successCount,
      failedCount,
      total: warmedUrls.length,
      urls: warmedUrls,
      reportDetailsWarmed: seenReportIds.size,
      interventionDetailsWarmed: seenIvDetailIds.size,
      entityDetailsWarmed,
      mediaWarmed: mediaWarmedCount,
      clientDocsListsWarmed,
      clientDocsMediaWarmed,
      messagingThreadsWarmed,
      messagingMessagesWarmed,
      deferredHeavyWarmed,
      limits,
      timestamp: Date.now()
    };
    _writeOfflineSnapshotMeta(snapshot);
    _dispatchOfflineEvent('ucp-offline-warmup-complete', snapshot);
  } finally {
    if (gen === _warmupGeneration) {
      _isWarmingOfflineCache = false;
    }
  }
}

export function getOfflineWarmupIntervalMs() {
  const override = Number(process.env.REACT_APP_OFFLINE_WARMUP_INTERVAL_MS);
  if (Number.isFinite(override) && override >= 0) return override;
  if (process.env.NODE_ENV === 'production') return OFFLINE_WARMUP_PROD_INTERVAL_MS;
  return OFFLINE_WARMUP_INTERVAL_MS;
}

/** DELETE + 404 : ressource déjà absente = objectif atteint (double envoi, synchro lente, premier essai déjà OK). */
function _offlineFlushTreatsAsSuccess(error, item) {
  const status = error?.response?.status;
  const method = String(item?.method || '').toLowerCase();
  return method === 'delete' && status === 404;
}

/** 4xx à réessayer plus tard (session / charge), pas à abandonner comme « données invalides ». */
function _offlineFlushShouldDefer(status) {
  return status === 401 || status === 408 || status === 429;
}

function _headersForOfflineReplay(item) {
  const h = { ...(item?.headers || {}) };
  delete h.Authorization;
  delete h.authorization;
  return h;
}

async function _flushOfflineQueue() {
  if (_isFlushingOfflineQueue) return;
  if (typeof navigator !== 'undefined' && !navigator.onLine) return;
  if (!_canReplayOfflineQueue()) return;

  _purgeDeniedMutationsFromOfflineQueue();

  _isFlushingOfflineQueue = true;
  try {
    const currentScope = _getOfflineScope();

    // 1) Outbox IndexedDB (ops JSON + multipart/photos)
    try {
      await flushTerrainOutbox(async (op) => {
        if (_isOfflineQueueDenied(op.url)) {
          _dispatchOfflineEvent('ucp-offline-sync-dropped', {
            id: op.clientOpId,
            url: op.url,
            method: op.method,
            status: null,
            message: 'Mutation critique / bruit présence retirée de la file hors-ligne (non rejouée automatiquement).',
          });
          return;
        }
        const data = await rebuildAxiosConfigData(op);
        const isMultipart = typeof FormData !== 'undefined' && data instanceof FormData;
        let response;
        try {
          response = await api({
            method: op.method,
            url: op.url,
            data,
            params: op.params || undefined,
            headers: {
              ..._headersForOfflineReplay(op),
              ...(isMultipart ? { 'Content-Type': 'multipart/form-data' } : {}),
            },
            _skipOfflineQueue: true,
            _skipOfflineCache: true,
          });
        } catch (error) {
          if (_offlineFlushTreatsAsSuccess(error, op)) {
            _dispatchOfflineEvent('ucp-offline-sync-success', {
              id: op.clientOpId,
              url: op.url,
              method: op.method,
              source: 'terrain-idb',
            });
            return;
          }
          throw error;
        }

        const workflowParsed = parseInterventionIdFromWorkflowUrl(op.url);
        if (workflowParsed?.id) {
          clearOptimisticInterventionStatus(workflowParsed.id);
        }

        // Rapport IV syncé : enchaîner validate-report si intervention connue
        if (op.opType === 'report_iv' && op.entityId && String(op.method || '').toLowerCase() === 'post') {
          const reportId = _extractReportIdFromResponseData(response?.data);
          if (reportId) {
            try {
              await api.post(
                `/intervenants/interventions/${op.entityId}/validate-report`,
                { rapportId: reportId },
                { _skipOfflineQueue: true, _skipOfflineCache: true }
              );
            } catch (validateErr) {
              console.warn(
                '[UCP] validate-report post-sync rapport offline:',
                validateErr?.response?.data || validateErr?.message || validateErr
              );
            }
          }
        }

        _dispatchOfflineEvent('ucp-offline-sync-success', {
          id: op.clientOpId,
          url: op.url,
          method: op.method,
          source: 'terrain-idb',
        });
      }, { scope: currentScope });
    } catch (idbFlushErr) {
      console.warn('[UCP] Flush outbox IDB:', idbFlushErr?.message || idbFlushErr);
    }

    // 2) Miroir localStorage legacy (JSON only) — items sans terrainOpId déjà syncé
    let queue = _readOfflineQueue();
    if (!Array.isArray(queue) || queue.length === 0) return;

    const pending = [];
    let idbPendingIds = new Set();
    try {
      const idbPending = await listTerrainOps({ status: 'pending', scope: currentScope });
      idbPendingIds = new Set(idbPending.map((o) => o.clientOpId));
    } catch {
      idbPendingIds = new Set();
    }

    for (const item of queue) {
      if (item.scope && item.scope !== currentScope) {
        pending.push(item);
        continue;
      }
      // Miroir IDB : ne jamais double-rejouer
      if (item.terrainOpId) {
        if (idbPendingIds.has(item.terrainOpId)) pending.push(item);
        continue;
      }
      if (_isOfflineQueueDenied(item.url)) {
        _dispatchOfflineEvent('ucp-offline-sync-dropped', {
          id: item.id,
          url: item.url,
          method: item.method,
          status: null,
          message: 'Mutation critique / bruit présence retirée de la file hors-ligne (non rejouée automatiquement).',
        });
        continue;
      }
      try {
        await api({
          method: item.method,
          url: item.url,
          data: item.data,
          params: item.params || undefined,
          headers: _headersForOfflineReplay(item),
          _skipOfflineQueue: true,
          _skipOfflineCache: true,
        });

        _dispatchOfflineEvent('ucp-offline-sync-success', { id: item.id, url: item.url, method: item.method });
      } catch (error) {
        const status = error?.response?.status;

        if (_offlineFlushTreatsAsSuccess(error, item)) {
          _dispatchOfflineEvent('ucp-offline-sync-success', { id: item.id, url: item.url, method: item.method });
          continue;
        }

        if (_offlineFlushShouldDefer(status)) {
          pending.push(item);
          const remaining = queue.slice(queue.indexOf(item) + 1);
          pending.push(...remaining);
          break;
        }

        if (status && status >= 400 && status < 500) {
          const body = error?.response?.data;
          const serverMsg =
            (typeof body?.message === 'string' && body.message) ||
            (typeof body?.error === 'string' && body.error) ||
            (typeof body?.error?.message === 'string' && body.error.message) ||
            undefined;
          _dispatchOfflineEvent('ucp-offline-sync-dropped', {
            id: item.id,
            url: item.url,
            method: item.method,
            status,
            message: serverMsg,
          });
          continue;
        }

        pending.push(item);
        const remaining = queue.slice(queue.indexOf(item) + 1);
        pending.push(...remaining);
        break;
      }
    }

    _writeOfflineQueue(pending);
  } finally {
    _isFlushingOfflineQueue = false;
  }
}

if (typeof window !== 'undefined') {
  const purged = _purgeDeniedMutationsFromOfflineQueue();
  if (purged > 0) {
    console.warn(
      `[UCP] ${purged} action(s) lourde(s) retirée(s) de la file hors-ligne (ex. generate-pdf).`
    );
  }
  window.addEventListener('online', () => {
    _scheduleOfflineFlush({ force: true });
  });
}

/** Nettoyage manuel / tests : retire generate-pdf et générations lourdes de la file locale. */
export function purgeHeavyOfflineQueueMutations() {
  return _purgeDeniedMutationsFromOfflineQueue();
}
// ─────────────────────────────────────────────────────────────────────────────

// ─── ✅ SECURITY-004 : Refresh token silencieux ───────────────────────────────
// Évite les appels simultanés à /auth/refresh si plusieurs requêtes expirent ensemble.
let _isRefreshing = false;
let _refreshQueue = []; // { resolve, reject }[]

function _processQueue(error, token = null) {
  _refreshQueue.forEach(({ resolve, reject }) => {
    if (error) reject(error);
    else resolve(token);
  });
  _refreshQueue = [];
}

/** Renouvellement silencieux (cookie ucp_refresh) — export pour bootstrap AuthContext. */
export async function attemptSilentRefresh() {
  if (_isRefreshing) {
    return new Promise((resolve, reject) => {
      _refreshQueue.push({ resolve, reject });
    });
  }
  _isRefreshing = true;
  try {
    return await _attemptRefreshWithRetry();
  } finally {
    _isRefreshing = false;
  }
}

/**
 * Avant API métier / au retour arrière-plan : refresh si JWT proche expiration.
 * Ne déconnecte pas sur échec réseau (laisse l'appelant / intercepteur 401 décider).
 */
export async function ensureFreshAccessToken(options = {}) {
  const bufferSec = Number.isFinite(options.bufferSec) ? options.bufferSec : JWT_RESUME_BUFFER_SEC;
  if (typeof window !== 'undefined' && isOnPublicAuthRoute(window.location.pathname)) {
    return _safeGetStorageItem('token');
  }
  const token = _safeGetStorageItem('token');
  if (token && !_isAccessTokenStale(token, bufferSec)) {
    return token;
  }
  const hasUser = Boolean(_safeGetStorageItem('user'));
  if (!token && !hasUser && !_hasRefreshCookieHint()) {
    return null;
  }
  return attemptSilentRefresh();
}

async function _attemptRefreshOnce() {
  // Appel au backend avec le cookie ucp_refresh (HttpOnly, envoyé automatiquement)
  const response = await axios.post(
    `${API_BASE_URL}/auth/refresh`,
    {},
    {
      withCredentials: true,
      timeout: AUTH_REQUEST_TIMEOUT_MS,
    }
  );
  const { token, user: refreshedUser } = response.data;
  if (refreshedUser && typeof refreshedUser === 'object') {
    const storedUser = _safeGetStorageItem('user');
    let merged = refreshedUser;
    if (storedUser) {
      try {
        merged = { ...JSON.parse(storedUser), ...refreshedUser };
      } catch {
        merged = refreshedUser;
      }
    }
    if (!safeLocalStorageSetItem('user', JSON.stringify(merged))) {
      try { sessionStorage.setItem('user', JSON.stringify(merged)); } catch { /* ignore */ }
    }
    if (typeof window !== 'undefined') {
      window.dispatchEvent(new CustomEvent('ucp-auth-user-updated', { detail: merged }));
    }
  }
  // Mettre à jour le localStorage + le header axios
  if (!safeLocalStorageSetItem('token', token)) {
    try {
      sessionStorage.setItem('token', token);
    } catch {
      // Ignorer: l'Authorization en mémoire reste utilisable tant que l'onglet est ouvert.
    }
  }
  setAuthToken(token);
  if (typeof document !== 'undefined' && token) {
    const secureAttr = window.location.protocol === 'https:' ? '; Secure' : '';
    document.cookie = `ucp_token=${encodeURIComponent(token)}; path=/; SameSite=Lax${secureAttr}`;
  }
  return token;
}

async function _attemptRefreshWithRetry() {
  let lastError;
  for (let attempt = 1; attempt <= REFRESH_MAX_ATTEMPTS; attempt += 1) {
    try {
      return await _attemptRefreshOnce();
    } catch (error) {
      lastError = error;
      if (isHardRefreshFailure(error)) {
        throw error;
      }
      if (attempt < REFRESH_MAX_ATTEMPTS && isTransientRefreshFailure(error)) {
        await _sleep(400 * attempt);
        continue;
      }
      throw error;
    }
  }
  throw lastError;
}

/** @deprecated alias interne — préférer _attemptRefreshWithRetry */
async function _attemptRefresh() {
  return _attemptRefreshWithRetry();
}

// ─────────────────────────────────────────────────────────────────────────────

// Intercepteur pour gérer les erreurs d'authentification et les erreurs réseau
api.interceptors.response.use(
  (response) => {
    recordApiSuccess();
    if (!response?.config?._skipOfflineCache) {
      _setCachedResponse(response.config, response.data);
      // GO_OFFLINE_MEDIA_API : peupler Cache API après lecture blob réussie.
      const cfg = response.config;
      if (
        String(cfg?.responseType || '').toLowerCase() === 'blob'
        && isInterventionReportImageContentPath(cfg?.url)
        && response.data
      ) {
        void putApiMediaCacheFromBlob(
          cfg.url,
          response.data,
          response.headers?.['content-type']
        );
      }
    }

    // Flush opportuniste (débouncé) — pas sur login ni API publiques.
    if (_shouldScheduleOfflineFlush(response?.config)) {
      _scheduleOfflineFlush();
    }
    return response;
  },
  async (error) => {
    const originalRequest = error.config;
    const status = error.response?.status;
    const requestUrl = originalRequest?.url || '';

    // Hors-ligne / réseau coupé: fallback lecture cachee pour GET (LS puis IDB).
    if (!error.response && _isGetRequest(originalRequest)) {
      const responseType = String(originalRequest?.responseType || '').toLowerCase();
      if (
        (responseType === 'blob' || isInterventionReportImageContentPath(originalRequest?.url))
      ) {
        const mediaHit = await matchApiMediaCache(originalRequest?.url);
        if (mediaHit) {
          const blob = await mediaHit.blob();
          _dispatchOfflineEvent('ucp-offline-media-hit', {
            url: originalRequest?.url,
            timestamp: Date.now(),
          });
          return Promise.resolve({
            data: blob,
            status: 200,
            statusText: 'OK (offline-media-cache)',
            headers: { 'content-type': mediaHit.headers.get('content-type') || blob.type || '' },
            config: originalRequest,
            request: null,
            fromOfflineCache: true,
            fromOfflineMediaCache: true,
          });
        }
      }

      const cached = await _getCachedResponseAsync(originalRequest);
      if (cached) {
        _dispatchOfflineEvent('ucp-offline-cache-hit', {
          url: originalRequest?.url,
          timestamp: cached.timestamp,
        });

        return Promise.resolve({
          data: cached.data,
          status: 200,
          statusText: 'OK (offline-cache)',
          headers: {},
          config: originalRequest,
          request: null,
          fromOfflineCache: true,
        });
      }
    }

    // Hors-ligne: mise en file des actions d'ecriture pour resynchronisation ulterieure.
    if (!error.response && _canQueueMutation(originalRequest, error)) {
      return _enqueueOfflineMutationResponse(originalRequest, error);
    }

    // Stabilisation production: un retry contrôlé pour GET sur 502/503/504/timeouts.
    if (_isRetryableGetRequest(error, originalRequest)) {
      recordGatewayFailure();
      const nextRetryCount = Number(originalRequest._retryCount || 0) + 1;
      await new Promise((resolve) => setTimeout(resolve, 700));
      return api({
        ...originalRequest,
        _retryCount: nextRetryCount,
        _skipOfflineQueue: true
      });
    }

    // Deploy / gateway : jusqu'à 2 relances pour mutations critiques (hors PDF / multipart).
    if (originalRequest && isRetryableCriticalMutation(error, originalRequest)) {
      const retried = await retryCriticalMutation(api, originalRequest, error);
      if (retried) return retried;
    } else if (
      originalRequest &&
      !_isGetRequest(originalRequest) &&
      (RETRYABLE_HTTP_STATUS.has(Number(error?.response?.status || 0)) || !error?.response)
    ) {
      recordGatewayFailure();
    }

    // 401 potentiellement recuperable: tenter un refresh unique pour limiter les deconnexions parasites.
    const shouldTryRefresh =
      status === 401 &&
      !originalRequest?._retried &&
      !_isAuthRoute(requestUrl);

    if (shouldTryRefresh) {
      originalRequest._retried = true; // empêcher les boucles infinies

      if (_isRefreshing) {
        // D'autres requêtes attendent déjà le refresh — on les met en file
        return new Promise((resolve, reject) => {
          _refreshQueue.push({ resolve, reject });
        }).then((token) => {
          originalRequest.headers['Authorization'] = `Bearer ${token}`;
          return api(originalRequest);
        });
      }

      _isRefreshing = true;
      try {
        const newToken = await _attemptRefreshWithRetry();
        _processQueue(null, newToken);
        originalRequest.headers['Authorization'] = `Bearer ${newToken}`;
        return api(originalRequest);
      } catch (refreshError) {
        _processQueue(refreshError, null);
        // Déconnexion uniquement sur échec refresh « dur » — pas sur réseau / timeout / 5xx.
        if (isHardRefreshFailure(refreshError)) {
          _forceAuthLogoutAndRedirect();
        }
        return Promise.reject(refreshError);
      } finally {
        _isRefreshing = false;
      }
    }

    // 401 après refresh réussi + retry : session réellement invalide.
    if (status === 401 && !_isAuthRoute(requestUrl) && originalRequest?._retried) {
      _forceAuthLogoutAndRedirect();
    }

    // Compte désactivé / supprimé / révoqué : JWT encore valide mais accès refusé côté API.
    const ACCOUNT_REVOKE_CODES = new Set(['ACCOUNT_DISABLED', 'ACCOUNT_REVOKED', 'INVALID_TOKEN']);
    if (status === 403 && !_isAuthRoute(requestUrl)) {
      const code = error.response?.data?.error?.code;
      if (code && ACCOUNT_REVOKE_CODES.has(String(code))) {
        _forceAuthLogoutAndRedirect();
        return Promise.reject(error);
      }
    }
    
    // ✅ AMÉLIORATION : Gérer les erreurs réseau (mobile/tablette)
    if (!error.response && error.message) {
      console.warn('⚠️ Erreur réseau détectée:', error.message);
      if (process.env.NODE_ENV !== 'production') {
        console.warn('💡 Vérifiez que le backend est démarré et accessible');
        console.warn('💡 Vérifiez la configuration CORS dans backend/server.js');
      }
    }
    
    return Promise.reject(error);
  }
);

// ✅ AMÉLIORATION : Intercepteur de requête pour timeout + reprise session avant API métier
api.interceptors.request.use(
  async (config) => {
    if (typeof FormData !== 'undefined' && config.data instanceof FormData) {
      const headers = { ...(config.headers || {}) };
      delete headers['Content-Type'];
      delete headers['content-type'];
      config.headers = headers;
    }

    // Timeout robuste:
    // - import/export/template : LONG_IMPORT_TIMEOUT_MS
    // - routes auth (login/refresh): marge plus large (cold start / hash password)
    // - autres routes: timeout standard
    if (!config.timeout) {
      const url = config?.url || '';
      if (_isLongRunningImportExportRoute(url)) {
        config.timeout = LONG_IMPORT_TIMEOUT_MS;
      } else if (_isHeavyListRoute(url)) {
        config.timeout = HEAVY_LIST_TIMEOUT_MS;
      } else {
        config.timeout = _isAuthRoute(url)
          ? AUTH_REQUEST_TIMEOUT_MS
          : DEFAULT_REQUEST_TIMEOUT_MS;
      }
    }

    // Au retour arrière-plan : renouveler le JWT avant l'appel métier si proche expiration.
    if (!config._skipSessionResume && !_isAuthRoute(config?.url || '')) {
      try {
        const freshToken = await ensureFreshAccessToken();
        if (freshToken) {
          config.headers = config.headers || {};
          config.headers.Authorization = `Bearer ${freshToken}`;
        }
      } catch {
        // Échec soft : la requête part ; l'intercepteur réponse gère un éventuel 401.
      }
    }

    if (_shouldScheduleOfflineFlush(config)) {
      _scheduleOfflineFlush();
    }
    return config;
  },
  (error) => {
    console.error('❌ Erreur lors de la préparation de la requête:', error);
    return Promise.reject(error);
  }
);

// Fonctions d'authentification
export const authAPI = {
  login: (credentials) => loginWithRetry(credentials),
  forgotPassword: (email) => api.post('/auth/forgot-password', { email }),
  resetPassword: ({ token, newPassword }) => api.post('/auth/reset-password', { token, newPassword }),
  getForgotPasswordConfig: () => api.get('/auth/forgot-password/config'),
  logout: () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    clearOfflineQueueOnSessionEnd();
  },
};

// Fonctions de gestion des administrateurs - CORRECTION DES ENDPOINTS
export const adminAPI = {
  // Récupérer tous les administrateurs
  getAdmins: () => api.get('/auth/superadmin/admins'),  // Ajout de /auth

  // Créer un nouvel administrateur
  createAdmin: (adminData) => api.post('/auth/superadmin/admins', adminData),  // Ajout de /auth

  // Mettre à jour un administrateur
  updateAdmin: (id, adminData) => api.put(`/auth/superadmin/admins/${id}`, adminData),  // Ajout de /auth

  // Supprimer un administrateur
  deleteAdmin: (id) => api.delete(`/auth/superadmin/admins/${id}`),  // Ajout de /auth

  // Renvoyer les identifiants (mot de passe provisoire) par email
  resendCredentials: (id) => api.post(`/auth/superadmin/admins/${id}/resend-credentials`),
};

// Fonctions de gestion des intervenants - NOUVEAU (même structure que adminAPI)
export const intervenantAPI = {
  // Récupérer tous les intervenants
  getAll: () => api.get('/auth/intervenants'), // ✅ Alias pour cohérence
  getIntervenants: () => api.get('/auth/superadmin/intervenants'),

  // Créer un nouvel intervenant
  createIntervenant: (intervenantData) => api.post('/auth/superadmin/intervenants', intervenantData),

  // Mettre à jour un intervenant
  updateIntervenant: (id, intervenantData) => api.put(`/auth/superadmin/intervenants/${id}`, intervenantData),

  // Supprimer un intervenant
  deleteIntervenant: (id) => api.delete(`/auth/superadmin/intervenants/${id}`),
  // Renvoyer les identifiants (mot de passe provisoire) par email
  resendCredentials: (id) => api.post(`/auth/superadmin/intervenants/${id}/resend-credentials`),
  // Récupérer les stats des intervenants
  getStats: () => api.get('/auth/superadmin/intervenants/stats'),
  /** Fiche intervenant par id y compris compte inactif (libellé assignation formulaire) */
  getReferenceForAssignment: (id) => api.get(`/auth/superadmin/intervenants/reference/${id}`),
};

// Fonctions de gestion des interventions - NOUVEAU (21 oct 2025)
export const interventionAPI = {
  // Récupérer toutes les interventions
  getAll: () => api.get('/interventions'),

  // Récupérer une intervention par ID
  getById: (id) => api.get(`/interventions/${id}`),

  // Créer une nouvelle intervention
  create: (interventionData) => api.post('/interventions', interventionData),

  // Mettre à jour une intervention
  update: (id, interventionData) => api.put(`/interventions/${id}`, interventionData),
  // Traiter une demande de reprogrammation client (staff)
  acceptReprogrammation: (id) => api.put(`/interventions/${id}/reprogrammation/accept`),
  refuseReprogrammation: (id) => api.put(`/interventions/${id}/reprogrammation/refuse`),

  // Supprimer une intervention
  delete: (id) => api.delete(`/interventions/${id}`),

  // Récupérer les stats des interventions
  getStats: () => api.get('/interventions/stats'),
};

// Fonctions de gestion des clients - NOUVEAU (21 oct 2025)
export const clientAPI = {
  // Récupérer tous les clients (params limit/fields/… — PERF_PLANNING 27AOUT2026)
  getAll: (params) => api.get('/clients', params ? { params } : undefined),

  // Récupérer un client par ID
  getById: (id) => api.get(`/clients/${id}`),

  // Créer un nouveau client
  create: (clientData) => api.post('/clients', clientData),

  // Mettre à jour un client
  update: (id, clientData) => api.put(`/clients/${id}`, clientData),

  // Supprimer un client
  delete: (id) => api.delete(`/clients/${id}`),

  // Récupérer le profil du client connecté (téléphone déchiffré inclus)
  getMyProfile: () => api.get('/client/profil'),
};

// Fonctions de gestion des sites - NOUVEAU (21 oct 2025)
export const planningNoteAPI = {
  getAll: (params) => api.get('/planning-notes', { params }),
  getById: (id) => api.get(`/planning-notes/${id}`),
  create: (data) => api.post('/planning-notes', data),
  update: (id, data) => api.put(`/planning-notes/${id}`, data),
  delete: (id) => api.delete(`/planning-notes/${id}`)
};

export const cartographieAPI = {
  getMapSites: (params) => api.get('/sites/map', { params }),
  getMapSummary: (id) => api.get(`/sites/${id}/map-summary`),
  geocodeSite: (id, { force = false } = {}) =>
    api.post(`/sites/${id}/geocode`, force ? { force: true } : {}, force ? { params: { force: 1 } } : undefined)
};

export const siteAPI = {
  // Récupérer tous les sites
  getAll: () => api.get('/sites'),

  // Récupérer les sites du client connecté
  getMine: () => api.get('/sites'),

  // Récupérer un site par ID
  getById: (id) => api.get(`/sites/${id}`),

  // Créer un nouveau site
  create: (siteData) => {
    if (hasSiteMediaFile(siteData)) {
      return api.post('/sites', buildSiteFormData(siteData));
    }
    return api.post('/sites', siteData);
  },

  // Mettre à jour un site
  update: (id, siteData) => {
    if (hasSiteMediaFile(siteData)) {
      return api.put(`/sites/${id}`, buildSiteFormData(siteData));
    }
    return api.put(`/sites/${id}`, siteData);
  },

  // Supprimer un site
  delete: (id) => api.delete(`/sites/${id}`),
};

export const profileAvatarAPI = {
  uploadAdminAvatar: (file) => {
    const formData = new FormData();
    formData.append('avatar', file);
    return api.post('/admins/profile/avatar', formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
  },
  uploadIntervenantAvatar: (file) => {
    const formData = new FormData();
    formData.append('avatar', file);
    return api.post('/intervenant/profil/avatar', formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
  },
  uploadClientAvatar: (file) => {
    const formData = new FormData();
    formData.append('avatar', file);
    return api.post('/client/profil/avatar', formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
  },
  clearAdminAvatar: () => api.put('/admins/profile', { avatar_url: null }),
  clearIntervenantAvatar: () => api.put('/intervenant/profil', { avatar_url: null }),
  clearClientAvatar: () => api.put('/client/profil', { avatar_url: null })
};

const parcBase = (interventionId) => `/intervenant/parc/${interventionId}`;

export const intervenantParcAPI = {
  getClient: (interventionId) => api.get(`${parcBase(interventionId)}/client`),
  updateClient: (interventionId, data) => api.put(`${parcBase(interventionId)}/client`, data),
  getSites: (interventionId) => api.get(`${parcBase(interventionId)}/sites`),
  getSite: (interventionId, siteId) => api.get(`${parcBase(interventionId)}/sites/${siteId}`),
  createSite: (interventionId, data) => api.post(`${parcBase(interventionId)}/sites`, data),
  createSiteMultipart: (interventionId, formData) => api.post(`${parcBase(interventionId)}/sites`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  }),
  updateSite: (interventionId, siteId, data) => api.put(`${parcBase(interventionId)}/sites/${siteId}`, data),
  updateSiteMultipart: (interventionId, siteId, formData) => api.put(`${parcBase(interventionId)}/sites/${siteId}`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  }),
  getEquipements: (interventionId) => api.get(`${parcBase(interventionId)}/equipements`),
  createEquipement: (interventionId, data) => api.post(`${parcBase(interventionId)}/equipements`, data),
  createEquipementMultipart: (interventionId, formData) => api.post(`${parcBase(interventionId)}/equipements`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  }),
  updateEquipement: (interventionId, equipementId, data) => api.put(`${parcBase(interventionId)}/equipements/${equipementId}`, data),
  updateEquipementMultipart: (interventionId, equipementId, formData) => api.put(`${parcBase(interventionId)}/equipements/${equipementId}`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  })
};

/** Invalide le cache hors-ligne GET pour une route (ex. après génération PDF). */
export function bustApiGetCache(urlPart) {
  const needle = String(urlPart || '').trim();
  if (!needle) return;
  try {
    const keys = [];
    for (let i = 0; i < localStorage.length; i += 1) {
      const key = localStorage.key(i);
      if (key && key.includes(OFFLINE_CACHE_PREFIX) && key.includes(needle)) {
        keys.push(key);
      }
    }
    keys.forEach((key) => localStorage.removeItem(key));
  } catch {
    // no-op
  }
}

// Fonctions de gestion des rapports d'intervention - NOUVEAU (21 oct 2025)
export const interventionReportAPI = {
  // Récupérer tous les rapports (force = no-cache HTTP ; clé offline sans _nc)
  getAll: (options = {}) => {
    const force = options.force === true;
    if (!force) return api.get('/intervention-reports');
    return api.get('/intervention-reports', {
      params: { _nc: Date.now() },
      headers: { 'Cache-Control': 'no-cache', Pragma: 'no-cache' }
    });
  },

  // Récupérer un rapport par ID
  getById: (id) => api.get(`/intervention-reports/${id}`),

  // Créer un nouveau rapport
  create: (reportData) => {
    const isMultipart = typeof FormData !== 'undefined' && reportData instanceof FormData;
    return api.post('/intervention-reports', reportData, isMultipart ? {
      headers: { 'Content-Type': 'multipart/form-data' }
    } : undefined);
  },

  // Mettre à jour un rapport
  update: (id, reportData) => {
    const isMultipart = typeof FormData !== 'undefined' && reportData instanceof FormData;
    return api.put(`/intervention-reports/${id}`, reportData, isMultipart ? {
      headers: { 'Content-Type': 'multipart/form-data' }
    } : undefined);
  },

  // Supprimer un rapport
  delete: (id) => api.delete(`/intervention-reports/${id}`),

  // Approuver un rapport (workflow validation)
  approve: (id, approvalData) => api.put(`/intervention-reports/${id}/approve`, approvalData),
};

// ✨ WORKFLOW INTERVENTIONS INTERVENANTS - Acceptation/Refus/Démarrage/Terminaison (20 FÉV 2026)
export const interventionWorkflowAPI = {
  // Accepter une intervention attribuée → Statut "Planifiée"
  accept: (interventionId) => api.put(`/intervenants/interventions/${interventionId}/accept`),

  // Refuser une intervention avec raison → Statut "annulee"
  reject: (interventionId, raison) => api.put(`/intervenants/interventions/${interventionId}/reject`, { raison }),

  // Démarrer une intervention acceptée → Statut "En Cours" + MAJ disponibilité intervenant
  start: (interventionId) => api.put(`/intervenants/interventions/${interventionId}/start`),

  // Terminer une intervention en cours → Redirection vers formulaire rapport (pas de changement statut)
  complete: (interventionId) => api.put(`/intervenants/interventions/${interventionId}/complete`),

  // Valider intervention après soumission rapport → Statut "Terminée" + MAJ disponibilité
  validateReport: (interventionId, rapportId) => api.post(`/intervenants/interventions/${interventionId}/validate-report`, { rapportId }),

  // Récupérer les interventions en attente (pour intervenant connecté)
  getPending: () => api.get('/intervenants/interventions/pending'),

  // Récupérer les interventions en cours (pour SuperAdmin/Admin, toutes les intervenants)
  getEnCours: () => api.get('/intervenants/interventions/en-cours'),
};

// CRUD Parametres SuperAdmin
export const parameterAPI = {
  getAll: () => api.get('/parameters'),
  getByCategory: (category) => api.get(`/parameters/${category}`),
  getByKey: (key) => api.get(`/parameters/key/${key}`),
  create: (payload) => api.post('/parameters', payload),
  updateById: (id, payload) => api.put(`/parameters/${id}`, payload),
  updateByKey: (key, payload) => api.put(`/parameters/key/${key}`, payload),
  deleteById: (id) => api.delete(`/parameters/${id}`),
  deleteByKey: (key) => api.delete(`/parameters/key/${key}`)
};

export default api;
// Fonctions pour le dashboard
export const dashboardAPI = {
  getStats: () => api.get('/auth/dashboard/stats'),
};
